import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useForm, FormProvider, useFieldArray } from 'react-hook-form'
import * as Modal from '_components/modal/generic-modal'
import { Loader } from '@refera/ui-web'
import { IconButton, FormLabel } from '@material-ui/core'
import useStyles from './styles'
import { useDialog } from '_/hooks/use-dialog'
import { useToast } from '_/hooks/use-toast'
import { NumericInput } from '_/views/budget-view-v2/components/Inputs/NumericInput/index'
import Autocomplete from '_components/inputs/Autocomplete'
import { Checkbox } from '_/components/inputs'
import { duplicateInspectionBudget } from '_modules/inspectionBudget/actions'
import { getInspectionTradesmenSelector } from '_modules/inspectionBudget/selector'
import { getCitiesOptions } from '_/modules/utils/selectors'
import { useInspectionLoading } from '_/views/inspection-budget/hooks/use-inspection-loading'
import { Trash } from '@refera/ui-icons'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import { duplicationModalHeaders } from '_/views/inspection-budget/utils/constants'
import { filterInspectionTradesmenByCity } from '_/views/inspection-budget/utils/functions'

const FormHeaders = ({ className, labelClassName, requiredClassName }) => {
  return (
    <div className={className}>
      {duplicationModalHeaders.map(({ key, label }) => (
        <FormLabel key={key} className={labelClassName}>
          {label}
          {label && <span className={requiredClassName}>*</span>}
        </FormLabel>
      ))}
    </div>
  )
}

export function DuplicateBudgetModal({ openModal, setOpenModal }) {
  const styles = useStyles()
  const dispatch = useDispatch()
  const { showToast } = useToast()
  const [loading, setLoading] = useState(false)
  const { showDialog, closeDialog } = useDialog()
  const { budget, setRelatedBudgets } = useInspectionLoading()
  const tradesmen = useSelector(getInspectionTradesmenSelector)
  const citiesOptions = useSelector(getCitiesOptions)

  const inspectionPercentages = useMemo(
    () => budget?.agency?.inspectionPercentages?.slice(1) ?? [],
    [budget?.agency?.inspectionPercentages]
  )

  const filteredTradesmen = useMemo(
    () =>
      filterInspectionTradesmenByCity({
        tradesmen: tradesmen?.results || [],
        city: budget?.city,
        state: budget?.state,
        citiesOptions,
      }),
    [budget?.city, budget?.state, citiesOptions, tradesmen?.results]
  )

  const isTradesmenCatalogLoading = !tradesmen || !citiesOptions?.length
  const isTradesmanSelectionBlocked = !budget?.city || isTradesmenCatalogLoading

  const methods = useForm({ defaultValues: { percentages: [] }, shouldUnregister: false })

  const {
    handleSubmit,
    watch,
    formState: { isDirty },
  } = methods

  const { fields, append, remove } = useFieldArray({
    name: 'percentages',
    control: methods.control,
  })

  const createDefaultPercentage = useCallback(() => {
    return {
      selected: false,
      referaPercentage: 0,
      agencyPercentage: 0,
      tradesman: null,
    }
  }, [])

  useEffect(() => {
    if (!openModal) return
    const data = inspectionPercentages?.length
      ? inspectionPercentages?.map(item => ({
          ...createDefaultPercentage(),
          referaPercentage: item.referaPercentage ?? 0,
          agencyPercentage: item.agencyPercentage ?? 0,
        }))
      : [createDefaultPercentage()]

    methods.reset({
      percentages: data,
    })
  }, [openModal, inspectionPercentages, createDefaultPercentage])

  const handleCancel = useCallback(() => {
    if (isDirty) {
      showDialog({
        type: 'warning',
        subject: 'Aviso',
        description: 'Você tem certeza que deseja cancelar estas alterações?',
        labelApprove: 'Sim',
        labelCancel: 'Não',
        onApprove: () => {
          closeDialog()
          methods.reset({ percentages: [] })
          setOpenModal(false)
        },
        onCancel: closeDialog,
      })
    } else {
      setOpenModal(false)
    }
  }, [isDirty, setOpenModal, showDialog, closeDialog])

  const onSubmit = useCallback(
    async data => {
      const selectedPercentages = data?.percentages.filter(p => p?.selected)

      if (!selectedPercentages?.length) {
        showToast({ type: 'error', message: 'Selecione pelo menos um percentual.' })
        return
      }
      setLoading(true)
      dispatch(
        duplicateInspectionBudget(budget?.id, { duplicationPercentages: selectedPercentages })
      )
        .then(res => {
          const duplicatedList = res?.data ?? []

          setRelatedBudgets(prev => [...duplicatedList, ...(prev ?? [])])
          setOpenModal(false)
          showToast({ type: 'success' })
        })
        .catch(e =>
          showToast({
            type: 'error',
            message: e?.error_message ?? 'Ocorreu um erro ao realizar a operação.',
          })
        )
        .finally(() => {
          setLoading(false)
        })
    },
    [dispatch, budget?.id, setOpenModal, showToast, setRelatedBudgets]
  )

  const handleAppend = useCallback(() => {
    if (fields.length < 4) {
      append({
        selected: false,
        referaPercentage: 0,
        agencyPercentage: 0,
        tradesman: null,
      })
    } else {
      showToast({
        type: 'warning',
        message: 'Você só pode inserir até 4 percentuais',
      })
    }
  }, [append, fields, showToast])

  const handleBlockedTradesmanClick = useCallback(
    event => {
      if (!isTradesmanSelectionBlocked) return

      event.preventDefault()
      event.stopPropagation()
      showToast({
        type: 'warning',
        message: isTradesmenCatalogLoading
          ? 'Aguarde o carregamento de cidades e prestadores para escolher um prestador.'
          : 'É necessário selecionar o endereço do imóvel antes de escolher um prestador.',
      })
    },
    [isTradesmanSelectionBlocked, isTradesmenCatalogLoading, showToast]
  )

  const handleRemove = useCallback(
    index => {
      if (fields?.length > 1) {
        showDialog({
          type: 'warning',
          subject: 'Você tem certeza que deseja executar esta ação?',
          onApprove: () => {
            remove(index)
            closeDialog()
          },
          onCancel: closeDialog,
          labelApprove: 'Confirmar',
          labelCancel: 'Cancelar',
        })
      } else {
        showToast({
          type: 'warning',
          message: 'Você precisa ter pelo menos 1 percentual definido.',
        })
      }
    },
    [remove, fields, showDialog, closeDialog, showToast]
  )

  return (
    <>
      <Loader hasBackdrop open={loading} />
      <Modal.Root
        key={budget?.id}
        className={styles.root}
        open={openModal}
        onOpenChange={setOpenModal}
        onClose={() => setOpenModal(false)}
      >
        <Modal.TitleModal title="Duplicar orçamento" />

        <Modal.Content>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} className={styles.content}>
              <FormHeaders
                className={styles.row}
                labelClassName={styles.label}
                requiredClassName={styles.required}
              />
              {fields?.map((item, index) => {
                const selected = watch(`percentages.${index}.selected`)

                return (
                  <div key={item.id} className={styles.row}>
                    <Checkbox
                      name={`percentages.${index}.selected`}
                      containerClasses={styles.checkContainer}
                    />

                    <NumericInput
                      name={`percentages.${index}.referaPercentage`}
                      defaultValue={item.referaPercentage ?? 0}
                      placeholder="0,00 %"
                      suffix=" %"
                      variant="standard"
                      rules={{
                        validate: value => {
                          if (selected && (value === null || value === undefined)) {
                            return 'Campo obrigatório'
                          }
                          return true
                        },
                      }}
                    />
                    <NumericInput
                      name={`percentages.${index}.agencyPercentage`}
                      defaultValue={item.agencyPercentage ?? 0}
                      placeholder="0,00 %"
                      suffix=" %"
                      variant="standard"
                      rules={{
                        validate: value => {
                          if (selected && (value === null || value === undefined)) {
                            return 'Campo obrigatório'
                          }
                          return true
                        },
                      }}
                    />
                    <div className={styles.fieldOverlayWrapper}>
                      <Autocomplete
                        name={`percentages.${index}.tradesman`}
                        options={filteredTradesmen}
                        defaultValue={item?.tradesman}
                        getOptionLabel={option => option.name || ''}
                        getOptionSelected={(option, value) => option.id === value?.id}
                        multiple={false}
                        loading={isTradesmenCatalogLoading}
                        autocompleteClasses={styles.compactAutocomplete}
                        required={selected}
                        disabled={isTradesmanSelectionBlocked}
                      />
                      {isTradesmanSelectionBlocked && (
                        <div
                          role="presentation"
                          className={styles.clickBlockOverlay}
                          onMouseDown={handleBlockedTradesmanClick}
                          onClick={handleBlockedTradesmanClick}
                        />
                      )}
                    </div>
                    <div className={styles.iconsContainer}>
                      <IconButton variant="ghost" onClick={() => handleRemove(index)}>
                        <Trash color="red" />
                      </IconButton>

                      <IconButton
                        variant="ghost"
                        onClick={handleAppend}
                        style={{ visibility: index === 0 ? 'visible' : 'hidden' }}
                      >
                        <AddCircleOutlineIcon style={{ color: '#1671ED' }} />
                      </IconButton>
                    </div>
                  </div>
                )
              })}
            </form>
          </FormProvider>
        </Modal.Content>

        <Modal.Actions>
          <Modal.ButtonRed onClick={handleCancel}>Cancelar</Modal.ButtonRed>
          <Modal.ButtonFullBlue onClick={handleSubmit(onSubmit)}>Criar</Modal.ButtonFullBlue>
        </Modal.Actions>
      </Modal.Root>
    </>
  )
}
